/**
 * Spatial-hash soft-separation tick — shared by Frontline / FFA / _template.
 * Domain knobs (radius formula, base repulsion, damping) stay in callers.
 */
import { SpatialHash2D } from "./spatial-hash-2d";
import {
  clampSepImpulse,
  considerTopK,
  lateralizeSepImpulse,
  type SepNeighbor,
} from "./soft-sep";

export type SoftSepSpatialOpts<T> = {
  items: readonly T[];
  hash: SpatialHash2D<T>;
  cellSize: number;
  getId: (t: T) => number;
  getXY: (t: T) => { x: number; y: number };
  /** Query disk radius for subject `a`. */
  queryRadius: (a: T) => number;
  /** Separation threshold; neighbors with d >= this are ignored. */
  sepRadiusFor: (a: T, b: T) => number;
  sepStrength: number;
  sepTopK: number;
  sepMaxImpulse: number;
  walkEnter: number;
  getIntent: (a: T) => { intentVx: number; intentVy: number };
  skipSubject?: (a: T) => boolean;
  skipPair?: (a: T, b: T) => boolean;
  /**
   * When d < 1e-4: call and do not add to top-k (Frontline direct nudge).
   * If omitted, tiny-d neighbors still enter top-k and use coincideImpulse.
   */
  onCoincide?: (a: T, b: T) => void;
  /** Impulse for tiny-d top-k neighbors (FFA). Default: id-hash angle × strength. */
  coincideImpulse?: (a: T, b: T) => { sx: number; sy: number };
  /** Extra force before lateralize (e.g. Frontline base repulsion). */
  extraForces?: (a: T) => { sx: number; sy: number };
  /** Scale after clamp (FFA restMul). Default 1. */
  impulseScale?: (a: T) => number;
  onIntegrate: (a: T, sx: number, sy: number) => void;
  scratchNear?: T[];
  scratchBuf?: SepNeighbor<T>[];
};

export function applySoftSepSpatial<T>(opts: SoftSepSpatialOpts<T>): void {
  const {
    items,
    hash,
    cellSize,
    getId,
    getXY,
    queryRadius,
    sepRadiusFor,
    sepStrength,
    sepTopK,
    sepMaxImpulse,
    walkEnter,
    getIntent,
    skipSubject,
    skipPair,
    onCoincide,
    coincideImpulse,
    extraForces,
    impulseScale,
    onIntegrate,
  } = opts;

  if (hash.getCellSize() !== cellSize) {
    hash.setCellSize(cellSize);
  }
  hash.clear();
  for (let i = 0; i < items.length; i++) {
    const e = items[i]!;
    const p = getXY(e);
    hash.insert(p.x, p.y, e);
  }

  const near = opts.scratchNear ?? ([] as T[]);
  const buf = opts.scratchBuf ?? ([] as SepNeighbor<T>[]);

  for (let i = 0; i < items.length; i++) {
    const a = items[i]!;
    if (skipSubject?.(a)) continue;

    buf.length = 0;
    near.length = 0;
    const ap = getXY(a);
    hash.queryDisk(ap.x, ap.y, queryRadius(a), near);

    for (let j = 0; j < near.length; j++) {
      const b = near[j]!;
      if (getId(a) === getId(b)) continue;
      if (skipPair?.(a, b)) continue;
      const bp = getXY(b);
      const d = Math.hypot(ap.x - bp.x, ap.y - bp.y);
      const sep = sepRadiusFor(a, b);
      if (d >= sep) continue;
      if (d < 1e-4 && onCoincide) {
        onCoincide(a, b);
        continue;
      }
      considerTopK(buf, { e: b, d }, sepTopK);
    }

    let sx = 0;
    let sy = 0;
    for (let n = 0; n < buf.length; n++) {
      const { e: b, d } = buf[n]!;
      const bp = getXY(b);
      const dx = ap.x - bp.x;
      const dy = ap.y - bp.y;
      const sep = sepRadiusFor(a, b);
      if (d < 1e-4) {
        const imp = coincideImpulse
          ? coincideImpulse(a, b)
          : defaultCoincideImpulse(getId(a), getId(b), sepStrength);
        sx += imp.sx;
        sy += imp.sy;
        continue;
      }
      const push = ((sep - d) / Math.max(sep, 1)) * sepStrength;
      const inv = 1 / Math.max(d, 1e-3);
      sx += dx * inv * push;
      sy += dy * inv * push;
    }

    if (extraForces) {
      const ex = extraForces(a);
      sx += ex.sx;
      sy += ex.sy;
    }

    const intent = getIntent(a);
    const prefSpd = Math.hypot(intent.intentVx, intent.intentVy);
    if (prefSpd >= walkEnter) {
      const lat = lateralizeSepImpulse(
        sx,
        sy,
        intent.intentVx,
        intent.intentVy,
        walkEnter,
      );
      sx = lat.sx;
      sy = lat.sy;
    }

    const cl = clampSepImpulse(sx, sy, sepMaxImpulse);
    const scale = impulseScale?.(a) ?? 1;
    onIntegrate(a, cl.sx * scale, cl.sy * scale);
  }
}

function defaultCoincideImpulse(
  idA: number,
  idB: number,
  sepStrength: number,
): { sx: number; sy: number } {
  const ang = ((idA * 17 + idB * 31) % 360) * (Math.PI / 180);
  return { sx: Math.cos(ang) * sepStrength, sy: Math.sin(ang) * sepStrength };
}
